import { HEADER_ORDERED_CULTURES, PaymentCodes } from '@constants/app.constants';
import { PaymentMethodResource } from '@models/resources/payment-method.model';
import { ProvinceStateResource } from '@models/resources/province-state.model';
import {
  createSelector,
  DefaultProjectorFn,
  MemoizedSelector,
} from '@ngrx/store';
import { selectResourceFeatureState } from '../reducers';
import {tryGetFlagUrl} from "@utils/flag-utils";
import {SelectOption} from "@components/controls/country-select/select-option.interface";

export const selectMonths = createSelector(
  selectResourceFeatureState,
  (state) => state.months,
);

export const selectGenders = createSelector(
  selectResourceFeatureState,
  (state) => state.genders,
);

export const selectCountries = createSelector(
  selectResourceFeatureState,
  (state) => state.countries,
);

export const selectCountriesAndMapToSelectModel = createSelector(
  selectResourceFeatureState,
  (state) => state.countries?.map((c) => {
    return {
      value: c.countryCode,
      displayText: c.name,
      imageUrl: tryGetFlagUrl(c.countryCode),
    } as SelectOption;
  }),
);

export const selectProvinceStates = createSelector(
  selectResourceFeatureState,
  (state) => state.provinceStates,
);

export const selectProvinceStatesByCountryCode = createSelector(
  selectProvinceStates,
  (provinceStates) => {
    return provinceStates?.reduce(
      (provinceStatesByCountryCode, provinceState) => {
        return {
          ...provinceStatesByCountryCode,
          [provinceState.countryCode]: [
            ...(provinceStatesByCountryCode[provinceState.countryCode] ?? []),
            provinceState,
          ],
        };
      },
      {} as { [countryCode: string]: ProvinceStateResource[] | undefined },
    );
  },
);

export const selectSuffixes = createSelector(
  selectResourceFeatureState,
  (state) => state.suffixes,
);

export const selectTimeZones = createSelector(
  selectResourceFeatureState,
  (state) => state.timeZones,
);

export const selectCurrencies = createSelector(
  selectResourceFeatureState,
  (state) => state.currencies,
);

export const selectPaymentMethods = createSelector(
  selectResourceFeatureState,
  (state) => state.paymentMethods,
);

export const selectCultures = createSelector(
  selectResourceFeatureState,
  (state) => state.cultures,
);

export const selectHeaderOrderedCultures = createSelector(
  selectResourceFeatureState,
  (state) => state.cultures
        ?.slice()
        .sort((a,b) => {
          return (
            (HEADER_ORDERED_CULTURES).indexOf(a.cultureCode) -
            (HEADER_ORDERED_CULTURES).indexOf(b.cultureCode)
          );
        }) ?? null
);

export const selectPhoneCodes = createSelector(
  selectResourceFeatureState,
  (state) => state.phoneCodes,
);

export const selectPhoneCodesAndMapToSelectModel = createSelector(
  selectResourceFeatureState,
  (state) => state.phoneCodes?.map((c) => {
    return {
      value: c.countryCode,
      displayText: c.name,
      imageUrl: tryGetFlagUrl(c.countryCode),
    } as SelectOption;
  }),
);

export const selectPaymentMethodByCode = (
  code: PaymentCodes,
): MemoizedSelector<
  unknown,
  PaymentMethodResource,
  DefaultProjectorFn<PaymentMethodResource>
> =>
  createSelector(selectPaymentMethods, (paymentMethods) => {
    if (code === PaymentCodes.CreditShell) {
      return {
        paymentMethodCode: PaymentCodes.CreditShell,
        name: 'Refund',
      } as PaymentMethodResource;
    }
    const match = paymentMethods?.find((m) => m.paymentMethodCode === code);
    return (
      match ??
      ({
        paymentMethodCode: PaymentCodes.Other,
        name: 'Other',
      } as PaymentMethodResource)
    );
  });
